import { useEffect, useState, Fragment } from "react";
import api, { formatMoney, formatDate, daysUntil, urgencyClass } from "@/lib/api";
import { useCompany } from "@/lib/company";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash, PencilSimple, CheckCircle, Sparkle, CaretDown, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import MoneyInput from "@/components/MoneyInput";

const ODEME_SEKLI = ["Nakit", "Havale/EFT", "Çek", "Kredi Kartı", "Diğer"];
const CURRENCIES = ["TRY", "USD", "EUR"];

const empty = {
  company_id: "", tip: "Alacak", proje: "", cari_unvan: "", hizmet_cinsi: "",
  anlasma_tutari: 0, currency: "TRY", odeme_sekli: "Havale/EFT",
  vade_sayisi: 1, taksitler: [],
  // legacy
  baslik: "", tutar: 0, vade_tarihi: "", durum: "Beklemede",
  banka: "", iban: "",
};

export default function CariEkstrePage() {
  const { companies, selectedCompany } = useCompany();
  const [items, setItems] = useState([]);
  const [tip, setTip] = useState("Alacak");
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    const parts = [];
    if (selectedCompany !== "all") parts.push(`company_id=${selectedCompany}`);
    parts.push(`tip=${tip}`);
    const { data } = await api.get(`/transactions?${parts.join("&")}`);
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selectedCompany, tip]);

  const openNew = () => {
    setForm({ ...empty, tip, company_id: selectedCompany !== "all" ? selectedCompany : companies[0]?.id || "" });
    setEditingId(null); setDialog(true);
  };
  const openEdit = (t) => { setForm({ ...empty, ...t }); setEditingId(t.id); setDialog(true); };

  const generatePlan = () => {
    if (!form.anlasma_tutari || form.anlasma_tutari <= 0) { toast.error("Anlaşma tutarı girin"); return; }
    if (form.vade_sayisi < 1) { toast.error("Vade sayısı 1 veya daha fazla olmalı"); return; }
    if (!form.vade_tarihi) { toast.error("İlk vade tarihini girin"); return; }
    const total = Number(form.anlasma_tutari);
    const n = Number(form.vade_sayisi);
    const per = Math.round((total / n) * 100) / 100;
    const rem = Math.round((total - per * (n - 1)) * 100) / 100;
    const [y, m, d] = form.vade_tarihi.split("-").map(Number);
    const arr = [];
    for (let i = 0; i < n; i++) {
      const dt = new Date(y, m - 1 + i, d);
      arr.push({
        tarih: dt.toISOString().slice(0, 10),
        tutar: i === n - 1 ? rem : per,
        odendi: false,
      });
    }
    setForm({ ...form, taksitler: arr, tutar: total });
    toast.success(`${n} taksit oluşturuldu (${formatMoney(per, form.currency)}/ay)`);
  };

  const save = async () => {
    if (!form.company_id) { toast.error("Şirket seçin"); return; }
    if (!form.cari_unvan) { toast.error("Cari ünvan girin"); return; }
    if (form.vade_sayisi === 0 && (!form.tutar || form.tutar <= 0)) {
      toast.error("Vade yok — tutar girin"); return;
    }
    if (form.vade_sayisi >= 1 && form.taksitler.length === 0) {
      toast.error("Ödeme planı oluşturun (Otomatik Üret butonu)"); return;
    }
    const payload = { ...form };
    // baslik legacy field
    payload.baslik = payload.hizmet_cinsi || payload.cari_unvan;
    payload.ticari_unvan = payload.cari_unvan;
    // toplam tutarı sync et
    if (payload.taksitler.length > 0) {
      payload.tutar = payload.taksitler.reduce((a, b) => a + Number(b.tutar), 0);
      payload.anlasma_tutari = payload.tutar;
      if (!payload.vade_tarihi) payload.vade_tarihi = payload.taksitler[0].tarih;
    } else {
      payload.anlasma_tutari = payload.tutar;
    }
    try {
      if (editingId) await api.put(`/transactions/${editingId}`, payload);
      else await api.post("/transactions", payload);
      toast.success("Kaydedildi");
      setDialog(false); load();
    } catch { toast.error("Hata"); }
  };

  const remove = async (id) => {
    if (!confirm("Silmek istediğinize emin misiniz?")) return;
    await api.delete(`/transactions/${id}`); load();
  };
  const complete = async (t) => {
    await api.put(`/transactions/${t.id}`, { ...t, durum: "Tamamlandı" });
    load();
  };

  const totalBekleyen = items
    .filter((t) => t.currency === "TRY" && t.durum === "Beklemede")
    .reduce((a, b) => a + (b.anlasma_tutari || b.tutar || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">Cari Ekstre</h1>
          <p className="text-sm text-muted-foreground mt-1">Proje bazlı alacak ve borç kayıtlarınız, ödeme planlarıyla.</p>
        </div>
        <Button onClick={openNew} data-testid="add-transaction-btn"><Plus size={16} className="mr-2" /> Yeni Kayıt</Button>
      </div>

      <div className="flex items-center gap-4">
        <Tabs value={tip} onValueChange={setTip}>
          <TabsList>
            <TabsTrigger value="Alacak" data-testid="tab-alacak">Alacak</TabsTrigger>
            <TabsTrigger value="Borç" data-testid="tab-borc">Borç</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="ml-auto text-sm text-muted-foreground">
          Toplam Anlaşma (TRY): <span className="font-semibold text-foreground tabular-nums">{formatMoney(totalBekleyen)}</span>
        </div>
      </div>

      <Card className="border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-6"></TableHead>
                <TableHead>Proje</TableHead>
                <TableHead>Cari Ünvan</TableHead>
                <TableHead>Hizmet</TableHead>
                <TableHead>Ödeme Şekli</TableHead>
                <TableHead>Vade</TableHead>
                <TableHead className="text-right">Anlaşma Tutarı</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Kayıt bulunmuyor.</TableCell></TableRow>
              )}
              {items.map((t) => {
                const taksitler = t.taksitler || [];
                const hasPlan = taksitler.length > 0;
                const anlasma = t.anlasma_tutari || t.tutar || 0;
                const isOpen = expanded[t.id];
                return (
                  <Fragment key={t.id}>
                    <TableRow data-testid={`tx-row-${t.id}`}>
                      <TableCell>
                        {hasPlan ? (
                          <button onClick={() => setExpanded({ ...expanded, [t.id]: !isOpen })}>
                            {isOpen ? <CaretDown size={16} /> : <CaretRight size={16} />}
                          </button>
                        ) : null}
                      </TableCell>
                      <TableCell className="font-medium">{t.proje || "-"}</TableCell>
                      <TableCell>{t.cari_unvan || t.ticari_unvan || "-"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{t.hizmet_cinsi || t.baslik}</TableCell>
                      <TableCell>
                        <Badge className={
                          t.odeme_sekli === "Çek" ? "badge-purple" :
                          t.odeme_sekli === "Nakit" ? "badge-yellow" : "badge-blue"
                        }>{t.odeme_sekli || "Havale/EFT"}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {hasPlan ? `${taksitler.length} taksit` : (
                          t.vade_tarihi ? formatDate(t.vade_tarihi) : "-"
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium">{formatMoney(anlasma, t.currency)}</TableCell>
                      <TableCell className="text-right space-x-1">
                        {t.durum !== "Tamamlandı" && (
                          <Button size="sm" variant="ghost" onClick={() => complete(t)}><CheckCircle size={15} className="urgency-green" /></Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => openEdit(t)}><PencilSimple size={15} /></Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(t.id)}><Trash size={15} className="text-destructive" /></Button>
                      </TableCell>
                    </TableRow>
                    {hasPlan && isOpen && (
                      <TableRow>
                        <TableCell colSpan={8} className="bg-secondary/20">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-2">
                            {taksitler.map((tk, i) => {
                              const d = daysUntil(tk.tarih);
                              return (
                                <div key={i} className={`p-2 rounded-md border text-sm ${tk.odendi ? "bg-secondary/50 opacity-60" : "bg-card"} border-border`}>
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs text-muted-foreground">#{i + 1}</span>
                                    {!tk.odendi && <Badge className={`text-[10px] ${urgencyClass(d)}`}>{d}g</Badge>}
                                  </div>
                                  <div className="tabular-nums">{formatDate(tk.tarih)}</div>
                                  <div className="tabular-nums font-medium">{formatMoney(tk.tutar, t.currency)}</div>
                                </div>
                              );
                            })}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Kaydı Düzenle" : `Yeni ${tip}`}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Şirket</Label>
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="form-company"><SelectValue placeholder="Seç..." /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tip</Label>
              <Select value={form.tip} onValueChange={(v) => setForm({ ...form, tip: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Alacak">Alacak</SelectItem>
                  <SelectItem value="Borç">Borç</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Proje Bilgisi</Label><Input data-testid="form-proje" value={form.proje} onChange={(e) => setForm({ ...form, proje: e.target.value })} /></div>
            <div><Label>Cari Ünvan</Label><Input data-testid="form-cari" value={form.cari_unvan} onChange={(e) => setForm({ ...form, cari_unvan: e.target.value })} /></div>
            <div className="col-span-2"><Label>Hizmet Cinsi</Label><Input data-testid="form-hizmet" value={form.hizmet_cinsi} onChange={(e) => setForm({ ...form, hizmet_cinsi: e.target.value })} /></div>
            <div>
              <Label>Anlaşma Tutarı</Label>
              <div className="flex gap-2">
                <MoneyInput data-testid="form-anlasma" value={form.anlasma_tutari}
                  onChange={(n) => setForm({ ...form, anlasma_tutari: n })} />
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Ödeme Şekli</Label>
              <Select value={form.odeme_sekli} onValueChange={(v) => setForm({ ...form, odeme_sekli: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ODEME_SEKLI.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vade Sayısı (0 = Vade Yok)</Label>
              <Input data-testid="form-vade-sayisi" type="number" min="0" value={form.vade_sayisi}
                onChange={(e) => setForm({ ...form, vade_sayisi: parseInt(e.target.value) || 0 })} />
            </div>
            <div>
              <Label>{form.vade_sayisi === 0 ? "Ödeme Tarihi" : "İlk Vade Tarihi"}</Label>
              <Input data-testid="form-vade-tarihi" type="date" value={form.vade_tarihi}
                onChange={(e) => setForm({ ...form, vade_tarihi: e.target.value })} />
            </div>

            {form.vade_sayisi === 0 && (
              <div className="col-span-2">
                <Label>Ödeme Tutarı (Vade Yok)</Label>
                <MoneyInput data-testid="form-tutar" value={form.tutar}
                  onChange={(n) => setForm({ ...form, tutar: n })} />
                <p className="text-xs text-muted-foreground mt-1">Vade yok — ödeme tutarını girin. Anlaşma tutarı aynı yapılır.</p>
              </div>
            )}

            {form.vade_sayisi >= 1 && (
              <div className="col-span-2 p-3 rounded-md bg-secondary/30 border border-border space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium flex items-center gap-2">
                    <Sparkle size={14} weight="duotone" className="text-primary" />
                    Otomatik Ödeme Planı ({form.vade_sayisi} taksit)
                  </div>
                  <Button size="sm" variant="outline" onClick={generatePlan} data-testid="generate-plan-btn">
                    Planı Oluştur
                  </Button>
                </div>
                {form.taksitler.length > 0 ? (
                  <div className="max-h-56 overflow-y-auto space-y-1">
                    {form.taksitler.map((tk, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <span className="text-muted-foreground w-8">#{i + 1}</span>
                        <Input type="date" className="flex-1" value={tk.tarih}
                          onChange={(e) => {
                            const copy = [...form.taksitler];
                            copy[i] = { ...copy[i], tarih: e.target.value };
                            setForm({ ...form, taksitler: copy });
                          }} />
                        <div className="w-40">
                          <MoneyInput value={tk.tutar}
                            onChange={(n) => {
                              const copy = [...form.taksitler];
                              copy[i] = { ...copy[i], tutar: n };
                              setForm({ ...form, taksitler: copy });
                            }} />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">Anlaşma tutarı ve ilk vade tarihi girip "Planı Oluştur" butonuna basın.</div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>İptal</Button>
            <Button onClick={save} data-testid="tx-save-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
